import { observer } from 'mobx-react'
import * as React from 'react'
import styles from './Prompt.sass'
import Avatar, { Badge } from 'components/Avatar'
import Button from 'components/Button'
import ReviewPromptDialogViewModel from 'jobs/prompts/review/ReviewPromptDialogViewModel'
import { PromptListItemViewModel } from '../PromptsListViewModel'
import ResponseValue from 'jobs/components/ResponseValue'
import { useObservableStream } from 'hooks/rx-hooks'
import { interval } from 'rxjs'
import { formatDuration } from 'utils/date-util'
import * as Duration from 'dration'
import cx from 'classnames'

interface Props {
  vm: PromptListItemViewModel
  reviewVM: ReviewPromptDialogViewModel
  padded?: boolean
}

export default observer(function Prompt({ vm, reviewVM, padded }: Props) {
  const pendingResponse = vm.prompt.isPendingResponse
  const canAnswerPrompt = vm.prompt.canAnswer

  return (
    <div
      className={cx(styles.root, padded && styles.padded)}
      data-test={'sidebar-prompt-item-' + vm.prompt.question?.question}
    >
      <div className={styles.container}>
        <Avatar
          size={32}
          subject={vm.prompt.createdBy}
          badge={!pendingResponse ? Badge.CheckMark : Badge.None}
        />
        <div className={styles.content}>
          <p className={styles.title}>{vm.prompt.question?.question}</p>
          {pendingResponse ? (
            <PromptDuration vm={vm}></PromptDuration>
          ) : (
            <p
              className={styles.description}
              data-test="sidebar-prompt-description"
            >
              <ResponseValue vm={vm.question} />
            </p>
          )}
        </div>
        <div className={styles.promptButton}>
          <Button
            type={canAnswerPrompt ? 'primary' : 'default'}
            onClick={() => reviewVM.viewPrompt(vm.prompt)}
            dataTest="sidebar-prompt-view-button"
          >
            {canAnswerPrompt ? 'Respond' : 'View'}
          </Button>
        </div>
      </div>
    </div>
  )
})

interface PromptDurationProps {
  vm: PromptListItemViewModel
}

const PromptDuration = observer(({ vm }: PromptDurationProps) => {
  useObservableStream(
    0,
    // Refreshes every 1 minutes
    interval(Duration.fromMinutes(1))
  )

  if (!vm.prompt.dateCreated) {
    return null
  }

  return (
    <p
      className={styles.descriptionPending}
      data-test="sidebar-prompt-duration"
    >
      {formatPromptDuration(vm.prompt.dateCreated)}
    </p>
  )
})

function formatPromptDuration(date: Date) {
  const diff = new Date().getTime() - date.getTime()

  if (diff >= Duration.fromMinutes(1)) {
    const duration = formatDuration(diff, 'long')
    return `Sent ${duration} ago`
  }

  return 'Sent less than a minute ago'
}
